import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Building2, Radio, ShieldCheck } from 'lucide-react';
import { partnerCategories } from '../data/partners';
import PartnerLogo from './PartnerLogo';
import { Section } from '../ui/layout/Section';
import { Tabs } from '../ui/navigation/Tabs';

const categoryIcons = {
  Banks: Building2,
  Telcos: Radio,
  Insurance: ShieldCheck,
};

const partnerLogosCount = partnerCategories.reduce(
  (total, category) => total + category.partners.length,
  0
);

const PartnerShowcase = () => {
  const [activeCategory, setActiveCategory] = useState(partnerCategories[0].title);
  const activePartners =
    partnerCategories.find((category) => category.title === activeCategory)?.partners ?? [];

  const tabItems = partnerCategories.map((category) => ({
    id: category.title,
    label: category.title,
    icon: categoryIcons[category.title],
    badge: category.partners.length,
  }));

  return (
    <Section
      background="muted"
      padding="xl"
      containerSize="lg"
      className="relative"
      header={{
        eyebrow: 'Trusted Partnerships',
        title: 'Integrated with industry leaders',
        description:
          "We collaborate with Ghana's leading banks, telcos, and insurers to deliver seamless financial solutions across the country.",
      }}
    >
      <div className="absolute inset-0 opacity-[0.35] pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary-100/40 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-accent-100/40 rounded-full blur-3xl" />
      </div>

      <div className="relative">
        <Tabs
          items={tabItems}
          value={activeCategory}
          onChange={setActiveCategory}
          layoutId="partner-showcase-tabs"
          ariaLabel="Partner categories"
          className="mb-10"
        />

        <AnimatePresence mode="wait">
          <motion.div
            key={activeCategory}
            role="tabpanel"
            id={`panel-${activeCategory}`}
            aria-labelledby={`tab-${activeCategory}`}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.25 }}
            className={`grid gap-5 md:gap-6 mx-auto max-w-5xl ${
              activePartners.length <= 3
                ? 'grid-cols-1 sm:grid-cols-3'
                : 'grid-cols-2 lg:grid-cols-4'
            }`}
          >
            {activePartners.length === 0 ? (
              <p className="col-span-full text-center text-gray-500 py-12">
                No partners in this category yet.
              </p>
            ) : (
              activePartners.map((partner, index) => (
                <motion.div
                  key={partner.alt}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3, delay: index * 0.06 }}
                >
                  <PartnerLogo partner={partner} />
                </motion.div>
              ))
            )}
          </motion.div>
        </AnimatePresence>

        <p className="text-center text-sm text-gray-400 mt-10">
          {partnerLogosCount} strategic partners across banking, telecom, and insurance
        </p>
      </div>
    </Section>
  );
};

export default PartnerShowcase;
